import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    DEFAULT_MAIL_NOTIFICATION_SETTINGS,
    getMailNotificationSettings,
    saveMailNotificationSettings,
    type MailNotificationSettings,
} from '@/utils/mailNotificationSettings';
import {
    DEFAULT_ADMIN_THEME_SETTINGS,
    applyAdminThemeSettingsToDocument,
    getAdminThemeSettings,
    saveAdminThemeSettings,
    type AdminThemeSettings,
} from '@/utils/adminThemeSettings';
import {
    DEFAULT_ORDER_PRINT_SETTINGS,
    getOrderPrintSettings,
    saveOrderPrintSettings,
    type OrderPrintSettings,
} from '@/utils/orderPrintSettings';

export default function AdminSettingsPage() {
    const { userId } = useAuth();
    const [settings, setSettings] = useState<MailNotificationSettings>(DEFAULT_MAIL_NOTIFICATION_SETTINGS);
    const [themeSettings, setThemeSettings] = useState<AdminThemeSettings>(DEFAULT_ADMIN_THEME_SETTINGS);
    const [orderPrintSettings, setOrderPrintSettings] = useState<OrderPrintSettings>(DEFAULT_ORDER_PRINT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [themeSaving, setThemeSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [themeStatusMessage, setThemeStatusMessage] = useState<string | null>(null);
    const [orderPrintSaving, setOrderPrintSaving] = useState(false);
    const [orderPrintStatusMessage, setOrderPrintStatusMessage] = useState<string | null>(null);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const [mailData, themeData, orderPrintData] = await Promise.all([
                    getMailNotificationSettings(),
                    getAdminThemeSettings(),
                    getOrderPrintSettings(),
                ]);
                setSettings(mailData);
                setThemeSettings(themeData);
                setOrderPrintSettings(orderPrintData);
                applyAdminThemeSettingsToDocument(themeData);
            } catch (error) {
                console.error('Error loading settings:', error);
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const handleToggle = () => {
        setSettings((prev) => ({
            ...prev,
            shipmentNotificationsEnabled: !prev.shipmentNotificationsEnabled,
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setStatusMessage(null);

        try {
            await saveMailNotificationSettings(settings);
            setStatusMessage('保存しました。');
        } catch (error) {
            console.error('Error saving mail notification settings:', error);
            setStatusMessage('保存に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    const updateTheme = (updater: (prev: AdminThemeSettings) => AdminThemeSettings) => {
        setThemeSettings((prev) => {
            const next = updater(prev);
            applyAdminThemeSettingsToDocument(next);
            return next;
        });
    };

    const handleThemeSave = async () => {
        setThemeSaving(true);
        setThemeStatusMessage(null);

        try {
            await saveAdminThemeSettings(themeSettings, userId || undefined);
            setThemeStatusMessage('デザイン設定を保存しました。');
        } catch (error) {
            console.error('Error saving admin theme settings:', error);
            setThemeStatusMessage('デザイン設定の保存に失敗しました。');
        } finally {
            setThemeSaving(false);
        }
    };

    const handleThemeReset = () => {
        setThemeSettings(DEFAULT_ADMIN_THEME_SETTINGS);
        applyAdminThemeSettingsToDocument(DEFAULT_ADMIN_THEME_SETTINGS);
        setThemeStatusMessage('デフォルト値を適用しました（未保存）。');
    };

    const updateOrderPrintSettings = (updater: (prev: OrderPrintSettings) => OrderPrintSettings) => {
        setOrderPrintSettings((prev) => updater(prev));
    };

    const handleOrderPrintSave = async () => {
        setOrderPrintSaving(true);
        setOrderPrintStatusMessage(null);

        try {
            await saveOrderPrintSettings(orderPrintSettings, userId || undefined);
            setOrderPrintStatusMessage('印刷設定を保存しました。');
        } catch (error) {
            console.error('Error saving order print settings:', error);
            setOrderPrintStatusMessage('印刷設定の保存に失敗しました。');
        } finally {
            setOrderPrintSaving(false);
        }
    };

    const handleOrderPrintReset = () => {
        setOrderPrintSettings(DEFAULT_ORDER_PRINT_SETTINGS);
        setOrderPrintStatusMessage('デフォルト値を適用しました（未保存）。');
    };

    return (
        <div className="p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">設定</h2>
                    <p className="mt-2 text-sm text-gray-600">システム全体のメール通知や管理画面デザインを管理します。</p>
                </div>
                <Link to="/admin" className="text-sm font-semibold text-emerald-600 hover:underline">
                    ホームに戻る
                </Link>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                <h3 className="text-lg font-bold text-gray-900">メール通知設定</h3>
                {loading ? (
                    <p className="text-sm text-gray-500">設定を読み込み中...</p>
                ) : (
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-800">発送/返送/認定証メール送信</p>
                            <p className="text-xs text-gray-500 mt-1">
                                注文ステータス更新時の通知メール送信をON/OFFできます。
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                                settings.shipmentNotificationsEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                            }`}
                            aria-pressed={settings.shipmentNotificationsEnabled}
                        >
                            <span
                                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                                    settings.shipmentNotificationsEnabled ? 'translate-x-7' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {saving ? '保存中...' : '設定を保存'}
                    </button>
                    {statusMessage && <span className="text-sm text-gray-600">{statusMessage}</span>}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                <h3 className="text-lg font-bold text-gray-900">注文印刷設定（task / exam）</h3>
                <p className="text-sm text-gray-600">
                    /admin/orders/*/task と /admin/orders/*/exam の印刷余白・項目解説・送付状タイトル文字サイズを調整します。
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">印刷ページ上下余白（mm）</span>
                        <input
                            type="number"
                            min={0}
                            max={30}
                            step={0.5}
                            value={orderPrintSettings.pageMarginTopBottomMm}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    pageMarginTopBottomMm: Number(e.target.value) || 0,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">A4内側 上余白（mm）</span>
                        <input
                            type="number"
                            min={0}
                            max={40}
                            step={0.5}
                            value={orderPrintSettings.pagePaddingTopMm}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    pagePaddingTopMm: Number(e.target.value) || 0,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">A4内側 下余白（mm）</span>
                        <input
                            type="number"
                            min={0}
                            max={40}
                            step={0.5}
                            value={orderPrintSettings.pagePaddingBottomMm}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    pagePaddingBottomMm: Number(e.target.value) || 0,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">項目解説の文字サイズ（pt）</span>
                        <input
                            type="number"
                            min={7}
                            max={20}
                            step={0.5}
                            value={orderPrintSettings.reviewFontSizePt}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    reviewFontSizePt: Number(e.target.value) || DEFAULT_ORDER_PRINT_SETTINGS.reviewFontSizePt,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">task送付状タイトル文字サイズ（pt）</span>
                        <input
                            type="number"
                            min={7}
                            max={32}
                            step={0.5}
                            value={orderPrintSettings.taskLetterTitleFontSizePt}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    taskLetterTitleFontSizePt:
                                        Number(e.target.value) || DEFAULT_ORDER_PRINT_SETTINGS.taskLetterTitleFontSizePt,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">exam送付状タイトル文字サイズ（pt）</span>
                        <input
                            type="number"
                            min={7}
                            max={32}
                            step={0.5}
                            value={orderPrintSettings.examLetterTitleFontSizePt}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    examLetterTitleFontSizePt:
                                        Number(e.target.value) || DEFAULT_ORDER_PRINT_SETTINGS.examLetterTitleFontSizePt,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">項目解説セル余白（px）</span>
                        <input
                            type="number"
                            min={0}
                            max={24}
                            step={1}
                            value={orderPrintSettings.reviewCellPaddingPx}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    reviewCellPaddingPx: Number(e.target.value) || 0,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">項目解説テキスト余白（px）</span>
                        <input
                            type="number"
                            min={0}
                            max={24}
                            step={1}
                            value={orderPrintSettings.reviewTextareaPaddingPx}
                            onChange={(e) =>
                                updateOrderPrintSettings((prev) => ({
                                    ...prev,
                                    reviewTextareaPaddingPx: Number(e.target.value) || 0,
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={handleOrderPrintSave}
                        disabled={orderPrintSaving || loading}
                        className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {orderPrintSaving ? '保存中...' : '印刷設定を保存'}
                    </button>
                    <button
                        type="button"
                        onClick={handleOrderPrintReset}
                        className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        デフォルトに戻す（未保存）
                    </button>
                    {orderPrintStatusMessage && (
                        <span className="self-center text-sm text-gray-600">{orderPrintStatusMessage}</span>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                <h3 className="text-lg font-bold text-gray-900">管理画面デザイン設定（分析ページ）</h3>
                <p className="text-sm text-gray-600">
                    文字サイズ・カラー・太さを保存して、コード変更なしで反映できます。プレビューは即時反映されます。
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">見出しカラー</span>
                        <input
                            type="color"
                            value={themeSettings.global.headingColor}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    global: { ...prev.global, headingColor: e.target.value },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-200"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">本文カラー</span>
                        <input
                            type="color"
                            value={themeSettings.global.bodyColor}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    global: { ...prev.global, bodyColor: e.target.value },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-200"
                        />
                    </label>

                    <label className="space-y-2 text-sm md:col-span-2">
                        <span className="font-semibold text-gray-700">見出し太さ（100-900）</span>
                        <input
                            type="number"
                            min={100}
                            max={900}
                            step={100}
                            value={themeSettings.global.headingWeight}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    global: {
                                        ...prev.global,
                                        headingWeight: Math.max(100, Math.min(900, Number(e.target.value) || 700)),
                                    },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">ページタイトルサイズ（CSS値）</span>
                        <input
                            type="text"
                            value={themeSettings.pages.analysis.pageTitleSize}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    pages: {
                                        ...prev.pages,
                                        analysis: { ...prev.pages.analysis, pageTitleSize: e.target.value },
                                    },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">セクションタイトルサイズ（CSS値）</span>
                        <input
                            type="text"
                            value={themeSettings.pages.analysis.sectionTitleSize}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    pages: {
                                        ...prev.pages,
                                        analysis: { ...prev.pages.analysis, sectionTitleSize: e.target.value },
                                    },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">KPI数値サイズ（CSS値）</span>
                        <input
                            type="text"
                            value={themeSettings.pages.analysis.kpiValueSize}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    pages: {
                                        ...prev.pages,
                                        analysis: { ...prev.pages.analysis, kpiValueSize: e.target.value },
                                    },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-300 px-3"
                        />
                    </label>

                    <label className="space-y-2 text-sm">
                        <span className="font-semibold text-gray-700">アクセントカラー</span>
                        <input
                            type="color"
                            value={themeSettings.pages.analysis.accentColor}
                            onChange={(e) =>
                                updateTheme((prev) => ({
                                    ...prev,
                                    pages: {
                                        ...prev.pages,
                                        analysis: { ...prev.pages.analysis, accentColor: e.target.value },
                                    },
                                }))
                            }
                            className="h-10 w-full rounded border border-gray-200"
                        />
                    </label>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={handleThemeSave}
                        disabled={themeSaving || loading}
                        className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {themeSaving ? '保存中...' : 'デザイン設定を保存'}
                    </button>
                    <button
                        type="button"
                        onClick={handleThemeReset}
                        className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        デフォルトに戻す（未保存）
                    </button>
                    {themeStatusMessage && <span className="self-center text-sm text-gray-600">{themeStatusMessage}</span>}
                </div>
            </div>
        </div>
    );
}
